import AppliedFilterChip from "components/ApplicationFilters/FiltersAndChips/BaseChipComponents/AppliedFilterChipBase";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import OrgOptionsStore from "stores/OrgOptionsStore";
import { useStore } from "utils/useStore";
import type BaseObjectStore from "../BaseObjectStore";
import type GroupingStore from "../ChartCard/ChartFeatureStores/GroupingStore";
import {
    getGroupedByChipText,
    GROUPED_BY_CHIP_PREFIX,
    type GroupedByChipSelection,
} from "./groupedByChipText";

/**
 * The slice of a base object this chip reads.
 *
 * Narrowed structurally rather than against `ChartCardStore`, because the chips row is shared with
 * the table card, whose store has no grouping at all -- and because a type-only import keeps this
 * file off ChartCardStore's import graph.
 */
type MaybeGroupedStore = { groupingStore?: GroupingStore };

/**
 * The text of a chart's "Grouped by" chip, or undefined when it has none.
 *
 * Resolves the grouping's own selection -- a module and question, a model, a metadata field -- from
 * the same stores the matching filters read, and warms the lazily loaded lists it needs. Every
 * loader here is cached and no-ops once loaded or in flight, so a dashboard of charts grouped by
 * the same module asks for it once.
 */
export const useGroupedByChipText = (
    store: BaseObjectStore<any, any>,
): string | undefined => {
    const filterValuesStore = useStore(ApplicationFilterValuesStore);
    const filterItemsStore = useStore(ApplicationFilterItemsStore);
    const optionsStore = useStore(OrgOptionsStore);

    const grouping = (store as MaybeGroupedStore).groupingStore?.activeGrouping;
    const categorical = grouping?.kind === "categorical" ? grouping : undefined;
    const numericRange =
        grouping?.kind === "numericRange" ? grouping : undefined;

    // A grouping only reaches `activeGrouping` fully configured, so each of these is present
    // whenever its dimension is the one in play. What may be missing is the *name* behind it.
    const questionModuleId =
        categorical?.option === "QuestionResult"
            ? categorical.licensedModuleId
            : undefined;
    const questionId =
        categorical?.option === "QuestionResult"
            ? categorical.questionId
            : undefined;
    const llmClassifierId =
        categorical?.option === "ModelResults"
            ? categorical.llmClassifierId
            : undefined;
    const metadataKey =
        categorical?.option === "ExtendedMetadata"
            ? categorical.extendedMetadataKey
            : undefined;
    const scoreModuleId =
        numericRange?.option === "ModuleScore"
            ? numericRange.moduleId
            : undefined;

    useEffect(() => {
        if (!questionModuleId) return;
        void filterValuesStore.loadQuestionResultFilterableModules();
        void filterValuesStore.loadModuleDetails(questionModuleId);
    }, [filterValuesStore, questionModuleId]);

    useEffect(() => {
        if (!scoreModuleId) return;
        void filterValuesStore.loadHierarchyModules();
    }, [filterValuesStore, scoreModuleId]);

    // Resolved during render rather than in a memo, so MobX tracks the store reads and the chip
    // re-renders when a list it is waiting on arrives. Same reason the group-by editors do.
    const selection: GroupedByChipSelection = {
        moduleName: questionModuleId
            ? filterValuesStore.questionResultFilterableModules.find(
                  (module) => module.id === questionModuleId,
              )?.name ??
              filterValuesStore.moduleDetailsById.get(questionModuleId)?.name
            : scoreModuleId
              ? filterValuesStore.hierarchyLicensedModules?.find(
                    (module) => module.id === scoreModuleId,
                )?.title
              : undefined,
        questionName:
            questionModuleId && questionId
                ? (() => {
                      const question = filterValuesStore.moduleDetailsById
                          .get(questionModuleId)
                          ?.questions?.find((item) => item.id === questionId);
                      return (
                          question?.shortQuestionText?.trim() ||
                          question?.questionText ||
                          undefined
                      );
                  })()
                : undefined,
        // OrgOptionsStore loads the model catalog eagerly for chart configuration; the filter
        // values store only loads it when the Model Results filter is opened. Prefer the eager one
        // and fall back, the way the grouping drilldown resolves the same name.
        modelName: llmClassifierId
            ? optionsStore.llmClassifiers.find(
                  (classifier) => classifier.id === llmClassifierId,
              )?.name ??
              filterValuesStore.llmClassifiers?.find(
                  (classifier) => classifier.id === llmClassifierId,
              )?.name
            : undefined,
        // The field's filter-picker entry carries its display title; the key itself is the raw
        // field name, which still names the right thing before that entry loads.
        metadataFieldName: metadataKey
            ? filterItemsStore.getFilterItem(`extendedMetadata.${metadataKey}`)
                  ?.title ?? metadataKey
            : undefined,
    };

    return getGroupedByChipText(grouping, selection);
};

type Props = {
    text: string;
};

/**
 * Names what a chart is grouped by, for the groupings where the chart alone cannot say.
 *
 * Styled as an applied filter chip and seated at the head of that row, but it is not a filter: it
 * never offers a remove button, and unlike the filter chips it stays in the chart's PNG export,
 * since an exported chart of "Yes / No / N/A" is exactly the case this chip exists for.
 */
export const GroupedByChip = observer(({ text }: Props) => (
    <AppliedFilterChip disabled isApplicable>
        {`${GROUPED_BY_CHIP_PREFIX}: ${text}`}
    </AppliedFilterChip>
));

export default GroupedByChip;
